<template>
  <div
    v-if="notice"
    class="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4"
    @keydown="onKeydown"
  >
    <div
      ref="dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="'notice-title-' + notice.id"
      class="w-full max-w-lg max-h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl overflow-hidden"
    >
      <div class="h-1.5 shrink-0" :class="tone.bar"></div>

      <div class="flex items-start justify-between gap-3 px-6 pt-5">
        <h2 :id="'notice-title-' + notice.id" class="text-lg font-semibold text-gray-900">{{ notice.title }}</h2>
        <button
          v-if="dismissible"
          type="button"
          class="-mr-2 -mt-1 rounded p-1 text-gray-400 hover:text-gray-700"
          aria-label="Fechar"
          :disabled="busy"
          @click="confirm"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <!-- Texto ja sanitizado pelo servidor (so formatacao: negrito, tamanho, cor, listas, link) -->
      <div class="notice-body overflow-y-auto px-6 py-4" v-html="notice.body_html"></div>

      <div class="shrink-0 border-t border-gray-100 px-6 py-4">
        <p v-if="error" class="mb-3 text-sm text-red-600" role="alert">{{ error }}</p>
        <div class="flex items-center justify-between gap-3">
          <span class="text-xs text-gray-400">
            <template v-if="queue.length > 1">Aviso 1 de {{ queue.length }}</template>
          </span>
          <button
            v-if="!readOnly"
            ref="primary"
            type="button"
            class="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
            :disabled="busy"
            @click="confirm"
          >
            {{ busy ? 'Enviando...' : notice.ack_label }}
          </button>
          <button
            v-else
            ref="primary"
            type="button"
            class="rounded-lg border border-amber-500 px-5 py-2.5 text-sm font-medium text-amber-700 hover:bg-amber-50"
            @click="hideForSupport"
          >
            Fechar (acesso de suporte)
          </button>
        </div>
        <p v-if="readOnly" class="mt-2 text-right text-xs text-gray-400">Somente leitura: o aviso continua pendente para o cliente.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { useNoticeStore } from '_app/stores/noticeStore';

const TONES = {
  info: { bar: 'bg-blue-600' },
  warning: { bar: 'bg-amber-500' },
  critical: { bar: 'bg-red-600' },
};

// Popup de aviso do admin ao lojista. Obrigatorios (importante/critico) nao tem como fechar sem confirmar;
// informativos tem X e "Entendi". A leitura vale pela loja e fica registrada no servidor.
// "Forcado" so na tela: nao bloqueia API, widget nem o funcionamento do concierge.
// Faixas (display "banner") sao mostradas pelo NoticeBanner; a fila e o store sao os mesmos.
export default {
  name: 'NoticeGate',

  components: { XMarkIcon },

  data() {
    return {
      store: useNoticeStore(),
      busy: false,
      error: '',
    };
  },

  computed: {
    queue() {
      return this.store.queue.filter((n) => n.display !== 'banner');
    },

    notice() {
      return this.store.modal;
    },

    tone() {
      return TONES[this.notice?.severity] || TONES.info;
    },

    readOnly() {
      return this.store.readOnly;
    },

    dismissible() {
      return this.notice && this.notice.display !== 'modal_blocking' && !this.readOnly;
    },
  },

  watch: {
    '$route'() {
      this.store.fetchPending();
    },

    'notice.id'(id) {
      document.body.classList.toggle('overflow-hidden', !!id);
      this.error = '';
      if (!id) return;
      this.store.markShown(id);
      this.$nextTick(() => this.$refs.primary?.focus());
    },
  },

  mounted() {
    this.store.fetchPending(true);
    this.onFocus = () => { if (document.visibilityState !== 'hidden') this.store.fetchPending(); };
    window.addEventListener('focus', this.onFocus);
    document.addEventListener('visibilitychange', this.onFocus);

    this.onNoticeEvent = () => this.store.fetchPending(true);
    if (this.$socket) {
      this.$socket.on('notice:new', this.onNoticeEvent);
      this.$socket.on('notice:cancelled', this.onNoticeEvent);
      this.$socket.on('connect', this.onNoticeEvent);
    }
  },

  beforeUnmount() {
    window.removeEventListener('focus', this.onFocus);
    document.removeEventListener('visibilitychange', this.onFocus);
    if (this.$socket) {
      this.$socket.off('notice:new', this.onNoticeEvent);
      this.$socket.off('notice:cancelled', this.onNoticeEvent);
      this.$socket.off('connect', this.onNoticeEvent);
    }
    document.body.classList.remove('overflow-hidden');
  },

  methods: {
    async confirm() {
      if (!this.notice || this.busy || this.readOnly) return;
      this.busy = true;
      this.error = '';
      const r = await this.store.ack(this.notice.id);
      if (r.error) this.error = 'Nao foi possivel confirmar agora. Tente novamente.';
      this.busy = false;
    },

    hideForSupport() {
      if (this.notice) this.store.hideForSupport(this.notice.id);
    },

    // Sem Esc e sem clique fora; Tab fica preso dentro do aviso
    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = this.$refs.dialog?.querySelectorAll('a[href], button:not([disabled])');
      if (!nodes || !nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style>
/* Texto formatado dos avisos (mesmas classes nt-* do painel admin; o servidor so aceita estas) */
.notice-body { font-size: 0.9375rem; line-height: 1.55; color: #1f2937; word-break: break-word; }
.notice-body p { margin: 0 0 0.6em; min-height: 1.2em; }
.notice-body ul { list-style: disc; padding-left: 1.4em; margin: 0 0 0.6em; }
.notice-body ol { list-style: decimal; padding-left: 1.4em; margin: 0 0 0.6em; }
.notice-body a { color: #2563eb; text-decoration: underline; }
.notice-body .nt-sm { font-size: 0.8125rem; }
.notice-body .nt-lg { font-size: 1.25rem; line-height: 1.35; }
.notice-body .nt-xl { font-size: 1.625rem; line-height: 1.3; font-weight: 600; }
.notice-body .nt-left { text-align: left; }
.notice-body .nt-center { text-align: center; }
.notice-body .nt-right { text-align: right; }
.notice-body .nt-justify { text-align: justify; }
.notice-body .nt-c-red { color: #dc2626; }
.notice-body .nt-c-orange { color: #ea580c; }
.notice-body .nt-c-green { color: #16a34a; }
.notice-body .nt-c-blue { color: #2563eb; }
.notice-body .nt-c-gray { color: #6b7280; }
</style>
